import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useServices } from '@/contexts/app-services';
import { useAuth } from '@/contexts/auth';
import type { ChatMessage, ToolCall } from '@/entities/chat-message';
import {
  generateConversationId,
  getMostRecentSuggestedTask,
  type SuggestedTask,
} from '@/entities/chat-message/operations';
import type { PlannedGoal } from '@/entities/planned-goal';
import type { Period } from '@/entities/types';
import {
  messageKeys,
  useAiMessages,
  useSendMessages,
  useUpdateBlockStatus,
} from '@/queries/message';
import {
  plannedGoalKeys,
  useCreateGoalAtPosition,
} from '@/queries/planned-goal';
import { logError } from '@/services/logger';
import {
  toDateTimeStringWithTimezone,
  toMidnightLocalString,
} from '@/utils/datetime';
import { useCompletePlan } from './useCompletePlan';
import { useParentContextPlan } from './useParentContextPlan';

interface UseGoalsCompanionParams {
  period: Period;
  targetStartAt: Date;
}

function isEventMessage(message: ChatMessage): boolean {
  return (
    message.role === 'user' &&
    message.blocks.length === 1 &&
    message.blocks[0]?.type === 'text' &&
    message.blocks[0].data.startsWith('Event: ')
  );
}

// Global tracking to prevent duplicate planStarted events
const sentPlanStartedEvents = new Set<string>();

/**
 * Clears the set of sent planStarted events. For testing only.
 */
export function clearSentPlanStartedEventsForTesting() {
  sentPlanStartedEvents.clear();
}

/**
 * Orchestrates the AI goals companion experience for a planning period.
 * Provides business context (current plan, parent plan, user info) to the AI
 * and manages the conversation lifecycle including the initial planStarted event.
 * Uses useAiMessages/useSendMessages for the underlying messaging mechanics.
 */
export function useGoalsCompanion({
  period,
  targetStartAt, // start of target period
}: UseGoalsCompanionParams) {
  const { user } = useAuth();
  const { analytics } = useServices();
  const conversationId = user
    ? generateConversationId(user.id, period, targetStartAt)
    : '';

  const currentStartAtLocal = toMidnightLocalString(targetStartAt);
  const { completePlan: currentPlan, isLoading: currentPlanLoading } =
    useCompletePlan(period, targetStartAt, currentStartAtLocal);

  const { parentContext, isLoading: parentLoading } = useParentContextPlan(
    period,
    targetStartAt,
  );
  const parentPlan = parentContext?.completePlan;

  const queryClient = useQueryClient();

  const { data: allMessages } = useAiMessages({ conversationId });
  const {
    sendEvent,
    sendMessage,
    resendConversation,
    persistAssistantMessage,
    isLoading: aiLoading,
  } = useSendMessages({ conversationId });
  const updateBlockStatus = useUpdateBlockStatus({ conversationId });
  const createGoalAtPriority = useCreateGoalAtPosition();

  // Event messages are persisted for recovery but hidden from consumers
  const messages = useMemo(
    () => allMessages.filter((m) => !isEventMessage(m)),
    [allMessages],
  );

  // Loading logic:
  // - If no messages: show loading until plans are loaded (for planStarted event)
  // - If messages exist: only show loading during AI operations
  const hasMessages = messages.length > 0;
  const plansLoading = currentPlanLoading || parentLoading;
  const isLoading = hasMessages ? aiLoading : aiLoading || plansLoading;

  const getContext = useCallback(
    (): object => ({
      currentPlan,
      parentPlan,
      user: {
        id: user?.id,
        currentDateTime: toDateTimeStringWithTimezone(),
      },
    }),
    [currentPlan, parentPlan, user?.id],
  );

  const addGoalToPlan = useCallback(
    async (title: string, parentGoalId?: string): Promise<void> => {
      if (!currentPlan) {
        throw new Error('Cannot add goal: plan not loaded');
      }

      const plannedGoals =
        queryClient.getQueryData<PlannedGoal[]>(
          plannedGoalKeys.byPlanId(currentPlan.id),
        ) ?? [];

      await createGoalAtPriority.mutateAsync({
        planId: currentPlan.id,
        goalCreateInput: { title, parentGoalId },
        insertAtPosition: plannedGoals.length,
        period,
        startAtLocal: currentStartAtLocal,
        source: 'aiSuggestion',
        trigger: 'aiSuggestion',
      });
    },
    [
      currentPlan,
      queryClient,
      createGoalAtPriority,
      period,
      currentStartAtLocal,
    ],
  );

  const acceptSuggestedTaskByTarget = useCallback(
    async (
      target: SuggestedTask,
      options?: { sendFollowUpEvent?: boolean },
    ): Promise<void> => {
      const currentMessages =
        queryClient.getQueryData<ChatMessage[]>(
          messageKeys.byConversationId(conversationId),
        ) ?? [];

      const { messageId, blockIndex } = target;
      const message = currentMessages.find((m) => m.id === messageId);
      const block = message?.blocks[blockIndex];

      if (!block || block.type !== 'entity') {
        return;
      }

      const { title, parentGoalId } = block.data;

      updateBlockStatus(messageId, blockIndex, 'added');
      analytics.trackAcceptedSuggestion({
        period,
        startAtLocal: currentStartAtLocal,
      });

      try {
        await addGoalToPlan(title, parentGoalId);
      } catch (error) {
        updateBlockStatus(messageId, blockIndex, 'suggested');
        throw error;
      }

      if (options?.sendFollowUpEvent === false) {
        return;
      }

      sendEvent.mutate(
        { eventType: 'taskAddedToPlan', context: getContext() },
        {
          onSuccess: (assistantMessage) => {
            const hasSuggestion = assistantMessage.blocks.some(
              (b) => b.type === 'entity' && b.status === 'suggested',
            );
            if (hasSuggestion) {
              analytics.trackReceivedSuggestion({
                period,
                startAtLocal: currentStartAtLocal,
              });
            }
          },
          onError: (error) => {
            logError(error, 'Failed to send taskAddedToPlan event');
          },
        },
      );
    },
    [
      queryClient,
      conversationId,
      updateBlockStatus,
      addGoalToPlan,
      sendEvent,
      getContext,
      analytics,
      period,
      currentStartAtLocal,
    ],
  );

  const skipSuggestedTaskByTarget = useCallback(
    async (
      target: SuggestedTask,
      options?: { sendFollowUpEvent?: boolean },
    ): Promise<void> => {
      const currentMessages =
        queryClient.getQueryData<ChatMessage[]>(
          messageKeys.byConversationId(conversationId),
        ) ?? [];

      const { messageId, blockIndex } = target;
      const message = currentMessages.find((m) => m.id === messageId);
      const block = message?.blocks[blockIndex];

      if (!block || block.type !== 'entity') {
        return;
      }

      updateBlockStatus(messageId, blockIndex, 'skipped');
      analytics.trackSkippedSuggestion({
        period,
        startAtLocal: currentStartAtLocal,
      });

      if (options?.sendFollowUpEvent === false) {
        return;
      }

      try {
        const assistantMessage = await sendEvent.mutateAsync({
          eventType: 'taskSkipped',
          context: getContext(),
        });
        const hasSuggestion = assistantMessage.blocks.some(
          (b) => b.type === 'entity' && b.status === 'suggested',
        );
        if (hasSuggestion) {
          analytics.trackReceivedSuggestion({
            period,
            startAtLocal: currentStartAtLocal,
          });
        }
      } catch (error) {
        updateBlockStatus(messageId, blockIndex, 'suggested');
        logError(
          error instanceof Error
            ? error
            : new Error('Failed to send taskSkipped event'),
          'Failed to send taskSkipped event',
        );
        throw error;
      }
    },
    [
      queryClient,
      conversationId,
      updateBlockStatus,
      sendEvent,
      getContext,
      analytics,
      period,
      currentStartAtLocal,
    ],
  );

  const acceptSuggestedTask = useCallback(
    async (options?: { sendFollowUpEvent?: boolean }): Promise<void> => {
      const currentMessages =
        queryClient.getQueryData<ChatMessage[]>(
          messageKeys.byConversationId(conversationId),
        ) ?? [];
      const task = getMostRecentSuggestedTask(currentMessages);

      if (!task) {
        return;
      }

      await acceptSuggestedTaskByTarget(task, options);
    },
    [queryClient, conversationId, acceptSuggestedTaskByTarget],
  );

  const skipSuggestedTask = useCallback(
    async (options?: { sendFollowUpEvent?: boolean }): Promise<void> => {
      const currentMessages =
        queryClient.getQueryData<ChatMessage[]>(
          messageKeys.byConversationId(conversationId),
        ) ?? [];
      const task = getMostRecentSuggestedTask(currentMessages);

      if (!task) {
        return;
      }

      await skipSuggestedTaskByTarget(task, options);
    },
    [queryClient, conversationId, skipSuggestedTaskByTarget],
  );

  const toolCallHandlers: Partial<Record<string, () => Promise<void>>> =
    useMemo(
      () => ({
        addSuggestedTaskToPlan: () =>
          acceptSuggestedTask({ sendFollowUpEvent: false }),
        skipSuggestedTask: () =>
          skipSuggestedTask({ sendFollowUpEvent: false }),
      }),
      [acceptSuggestedTask, skipSuggestedTask],
    );

  const executeToolCalls = useCallback(
    async (toolCalls: ToolCall[]): Promise<void> => {
      for (const toolCall of toolCalls) {
        const handler = toolCallHandlers[toolCall.name];

        if (!handler) {
          logError(
            new Error(`Unknown tool call: ${toolCall.name}`),
            'Skipping unrecognized tool call',
          );
          continue;
        }

        try {
          await handler();
        } catch (error) {
          logError(error, `Tool call ${toolCall.name} failed`);
        }
        return;
      }
    },
    [toolCallHandlers],
  );

  // Send initial planStarted event when plans have loaded and conversation hasn't been started
  useEffect(() => {
    // Skip if conversation already had its initial event sent
    if (sentPlanStartedEvents.has(conversationId) || !user) {
      return;
    }

    // Skip if we don't have the required plan data yet
    if (!currentPlan || parentLoading) {
      return;
    }

    const context = {
      currentPlan,
      parentPlan,
      user: {
        id: user.id,
        currentDateTime: toDateTimeStringWithTimezone(),
      },
    };

    // No messages — send initial planStarted event
    if (allMessages.length === 0) {
      // Mark as sent immediately to prevent duplicates
      sentPlanStartedEvents.add(conversationId);

      // WARNING: setTimeout required to avoid Tanstack Query not updating mutation.isPending
      // https://github.com/TanStack/query/issues/5341 -- Mutations can not be started from an effect in Reacts StrictMode #5341
      setTimeout(() => {
        sendEvent.mutate(
          { eventType: 'planStarted', context },
          {
            onError: (error) => {
              logError(error, 'Failed to send initial planStarted event');
            },
          },
        );
      }, 0);
      return;
    }

    // Conversation is healthy — last message has an AI response
    const lastMessage = allMessages[allMessages.length - 1];

    if (lastMessage?.role === 'assistant') {
      sentPlanStartedEvents.add(conversationId);
      return;
    }

    // Last message is user — AI never responded (e.g. browser refreshed mid-flight).
    // Skip if a mutation is already in-flight to avoid duplicate resends.
    if (aiLoading) {
      return;
    }

    sentPlanStartedEvents.add(conversationId);

    setTimeout(() => {
      resendConversation.mutate(
        { context },
        {
          onSuccess: (data) => {
            const hasSuggestion = data.message.blocks.some(
              (block) =>
                block.type === 'entity' && block.status === 'suggested',
            );
            if (hasSuggestion) {
              analytics.trackReceivedSuggestion({
                period,
                startAtLocal: currentStartAtLocal,
              });
            }
            if (data.toolCalls.length > 0) {
              void executeToolCalls(data.toolCalls);
            }
            persistAssistantMessage(data.message);
          },
          onError: (error) => {
            logError(error, 'Failed to resend conversation after reload');
          },
        },
      );
    }, 0);
  }, [
    conversationId,
    sendEvent,
    resendConversation,
    persistAssistantMessage,
    currentPlan,
    parentPlan,
    parentLoading,
    user,
    period,
    allMessages,
    aiLoading,
    executeToolCalls,
    analytics,
    currentStartAtLocal,
  ]);

  function handleSendMessage(content: string): void {
    analytics.trackSentMessage({
      period,
      startAtLocal: currentStartAtLocal,
      conversationMessageCount: messages.length,
    });

    sendMessage.mutate(
      { content, context: getContext() },
      {
        onSuccess: (data) => {
          const hasSuggestion = data.message.blocks.some(
            (block) => block.type === 'entity' && block.status === 'suggested',
          );
          if (hasSuggestion) {
            analytics.trackReceivedSuggestion({
              period,
              startAtLocal: currentStartAtLocal,
            });
          }
          if (data.toolCalls.length > 0) {
            void executeToolCalls(data.toolCalls);
          }
          persistAssistantMessage(data.message);
        },
      },
    );
  }

  return {
    conversationId,
    messages,
    isLoading,
    sendMessage: handleSendMessage,
    sendError: sendMessage.error,
    isSending: sendMessage.isPending,
    acceptSuggestedTask,
    skipSuggestedTask,
    acceptSuggestedTaskByTarget,
    skipSuggestedTaskByTarget,
  };
}
